import { Typography, Box, makeStyles } from "@material-ui/core";
import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { RegistrationMethod } from "@/api/generated";
import { sendPhoneNumberAuthCode } from "@/api/sendPhoneNumberAuthCode";
import { CustomForm } from "@/components/Form";
import { Loading } from "@/components/Loading";
import { RegistrationHelpText } from "@/components/registration/RegistrationHelpText";
import { RegistrationPhases } from "@/components/registration/RegistrationPhases";
import { RegistrationFlowLayout as EntranceTemplate, RegistrationActions } from "@/layouts/RegistrationFlowLayout";
import theme from "@/theme";
import { RegisterFullFormData } from "@/validations/register";
import {
  RegistrationMethodState,
  UserRegistrationsAppleFormState,
  UserRegistrationsFormState,
  UserRegistrationsGoogleFormState,
} from "@/view_models/atoms";

const useStyles = makeStyles(() => ({
  error: {
    color: theme.palette.error.main,
    fontSize: theme.typography.caption.fontSize,
  },
}));

interface RegisterPhoneNumberPageProps {
  // 429（同じ番号へ送信済み）で次へ進む場合は、認証コード画面の再送カウントダウンに引き継ぐ残り秒数を渡す
  onNext: (resendCooldownSeconds?: number, existingCodeAvailable?: boolean) => void;
  onBack: () => void;
  validate: () => Promise<boolean>;
}

enum NumberVerificationStatus {
  INITIAL = 0, // 初期状態
  VERIFYING = 1, // 認証中
}

const RegisterPhoneNumberPage: React.FC<RegisterPhoneNumberPageProps> = ({ onNext, onBack, validate }) => {
  const [error, setError] = React.useState<string>("");
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext<RegisterFullFormData>();
  const [formData, setFormData] = useRecoilState(UserRegistrationsFormState);
  const setGoogleFormData = useSetRecoilState(UserRegistrationsGoogleFormState);
  const setAppleFormData = useSetRecoilState(UserRegistrationsAppleFormState);
  const registrationMethod = useRecoilValue(RegistrationMethodState);
  const [isNumberVerified, setIsNumberVerified] = React.useState<NumberVerificationStatus>(
    NumberVerificationStatus.INITIAL
  );

  const formSetters = {
    [RegistrationMethod.Email]: setFormData,
    [RegistrationMethod.Google]: setGoogleFormData,
    [RegistrationMethod.Apple]: setAppleFormData,
  } as const;

  const classes = useStyles();

  const handleNext = React.useCallback(async () => {
    // 認証中画面の表示
    setIsNumberVerified(NumberVerificationStatus.VERIFYING);

    // バリデーション
    const isValid = await validate();
    if (!isValid) {
      // 認証中画面を戻す
      setIsNumberVerified(NumberVerificationStatus.INITIAL);
      return;
    }
    // 電話番号を送信
    const data = getValues();
    const result = await sendPhoneNumberAuthCode(data.phone_number);
    // 429 は他のセッションや送信中の予約でも返る。API がこのセッションのコードを確認した場合だけ次へ進む。
    if (result.success || result.codeAvailable === true) {
      formSetters[registrationMethod]((prev) => ({
        ...prev,
        phone_number: data.phone_number,
      }));
      onNext(result.retryAfter, result.codeAvailable === true);
    } else {
      setError(result.error);
    }
    // 認証中画面を戻す
    setIsNumberVerified(NumberVerificationStatus.INITIAL);
  }, [getValues, onNext, validate, formData, registrationMethod]);

  // UIのレンダリング処理
  const renderContent = () => {
    switch (isNumberVerified) {
      case NumberVerificationStatus.VERIFYING:
        return <Loading />;
      default:
        return null;
    }
  };

  return (
    <EntranceTemplate
      phaseTags={<RegistrationPhases completed />}
      title={
        <>
          不正ログイン対策として、
          <br />
          SMS認証を導入しています
        </>
      }
      description={"パスワードに加えてSMS認証を組み合わせ、\n二段階でアカウントを保護します。"}
      descriptionSize="small"
    >
      {error && (
        <Typography variant="caption" className={classes.error} role="alert">
          {error}
        </Typography>
      )}
      <Box mt={4}>
        <Box>
          <Controller
            name="phone_number"
            control={control}
            defaultValue={formData.phone_number || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="電話番号"
                type="text"
                inputMode="tel"
                helperText="ハイフンなしで入力してください"
                helperTextPlacement="below"
                error={!!errors.phone_number}
                errorText={errors.phone_number?.message}
                placeHolder="09012345678"
              />
            )}
          />
          <Box mt={2}>
            <RegistrationHelpText>この後、SMSで届く6桁のコードを入力してください。</RegistrationHelpText>
          </Box>
          <RegistrationActions
            onNext={handleNext}
            onBack={onBack}
            label="認証コードを送信する"
            disabled={isNumberVerified !== NumberVerificationStatus.INITIAL}
          />
        </Box>
      </Box>
      {isNumberVerified !== NumberVerificationStatus.INITIAL && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          bgcolor="rgba(0, 0, 0, 0.5)"
        >
          {renderContent()}
        </Box>
      )}
    </EntranceTemplate>
  );
};

export default RegisterPhoneNumberPage;
